import { useEffect, useMemo, useState } from 'react';
import { FunnelInsightsApiClient } from '../api/insightsClient';
import type {
  FunnelDropoffEdgeData,
  FunnelInsightFilters,
  FunnelInsightWarning,
} from '../types';

interface UseFunnelEdgeDrilldownInput {
  projectId: string;
  onboardingId: string;
  versionId: string;
  canvasTransitionId?: string;
  enabled: boolean;
  filters?: FunnelInsightFilters;
}

interface UseFunnelEdgeDrilldownResult {
  data: FunnelDropoffEdgeData | null;
  loading: boolean;
  error: string | null;
  warnings: FunnelInsightWarning[];
}

export function useFunnelEdgeDrilldown(input: UseFunnelEdgeDrilldownInput): UseFunnelEdgeDrilldownResult {
  const [data, setData] = useState<FunnelDropoffEdgeData | null>(null);
  const [warnings, setWarnings] = useState<FunnelInsightWarning[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const client = useMemo(() => new FunnelInsightsApiClient(), []);
  const filtersKey = JSON.stringify(input.filters ?? {});
  const canLoad = input.enabled && Boolean(
    input.projectId &&
    input.onboardingId &&
    input.versionId &&
    input.canvasTransitionId,
  );

  useEffect(() => {
    if (!canLoad || !input.canvasTransitionId) {
      setData(null);
      setWarnings([]);
      setLoading(false);
      setError(null);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    client.getDropoffEdge({
      projectId: input.projectId,
      onboardingId: input.onboardingId,
      versionId: input.versionId,
      canvasTransitionId: input.canvasTransitionId,
      filters: input.filters,
      signal: controller.signal,
    }).then((response) => {
      setData(response.data);
      setWarnings(response.warnings ?? []);
    }).catch((err) => {
      if (controller.signal.aborted) return;
      setData(null);
      setWarnings([]);
      setError(err instanceof Error ? err.message : 'Failed to load edge insights');
    }).finally(() => {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    });

    return () => controller.abort();
  }, [
    canLoad,
    client,
    input.projectId,
    input.onboardingId,
    input.versionId,
    input.canvasTransitionId,
    filtersKey,
  ]);

  return { data, loading, error, warnings };
}
